import { format, addDays } from 'date-fns';

// ─── Variable Resolver ────────────────────────────────────────────────────────

export function resolveVariables(company, employee, user, overrides = {}) {
  const emp = employee || {};
  const firstName = emp.first_name || '';
  const lastName  = emp.last_name  || '';
  const empName   = [firstName, lastName].filter(Boolean).join(' ') || emp.name || '';
  const deptName  = typeof emp.department  === 'string' ? emp.department  : (emp.department?.name  || '');
  const desigName = typeof emp.designation === 'string' ? emp.designation : (emp.designation?.name || '');
  const gross     = emp.salary?.gross_monthly;
  const basic     = emp.salary?.basic;

  const defaults = {
    company_name:        company?.name        || 'Your Company',
    company_address:     company?.address     || 'Company Address',
    company_city:        company?.city        || '',
    company_state:       company?.state       || '',
    company_phone:       company?.phone       || '',
    company_email:       company?.email       || '',
    document_date:       format(new Date(), 'dd MMMM yyyy'),
    document_year:       new Date().getFullYear().toString(),
    employee_name:       empName,
    employee_code:       emp.employee_code    || '',
    designation:         desigName,
    department:          deptName,
    date_of_joining:     emp.joining_date ? format(new Date(emp.joining_date), 'dd MMMM yyyy') : '___________',
    date_of_birth:       emp.extended_details?.dob ? format(new Date(emp.extended_details.dob), 'dd MMMM yyyy') : '___________',
    employee_address:    emp.extended_details?.current_address || '___________',
    gross_salary:        gross ? `₹${gross.toLocaleString('en-IN')} per month` : '₹___________ per month',
    basic_salary:        basic ? `₹${basic.toLocaleString('en-IN')} per month` : '₹___________ per month',
    annual_ctc:          gross ? `₹${(gross * 12).toLocaleString('en-IN')} per annum` : '₹___________ per annum',
    manager_name:        emp.reporting_manager?.name       || '___________',
    manager_designation: emp.reporting_manager?.designation || 'Reporting Manager',
    hr_signatory:        user?.name   || '___________',
    hr_designation:      user?.role   || 'HR Manager',
    effective_date:      format(addDays(new Date(), 1), 'dd MMMM yyyy'),
    last_working_day:    '___________',
    notice_period:       '30 days',
    probation_period:    '6 months',
    confirmation_date:   format(addDays(new Date(), 1), 'dd MMMM yyyy'),
    new_designation:     '___________',
    new_department:      '___________',
    new_location:        '___________',
    revised_salary:      '₹___________',
    increment_amount:    '₹___________',
    increment_percent:   '___%',
    loan_amount:         '₹___________',
    loan_emi:            '₹___________',
    loan_months:         '__ months',
    warning_subject:     '___________',
    warning_incident:    '___________',
    termination_reason:  '___________',
    reply_by_date:       format(addDays(new Date(), 7), 'dd MMMM yyyy'),
    reference_no:        `REF/${new Date().getFullYear()}/${String(Date.now()).slice(-4)}`,
    years_of_service:    '___________',
    employment_type:     emp.employment_type || 'Full-Time',
  };

  return { ...defaults, ...overrides };
}

export function fillTemplate(html, variables) {
  return html.replace(/\{\{(\w+)\}\}/g, (_, key) => variables[key] ?? `{{${key}}}`);
}

// ─── Shared letterhead builder ────────────────────────────────────────────────
function letterhead() {
  return `
  <table width="100%" cellpadding="0" cellspacing="0" style="border-bottom: 2px solid #1e293b; padding-bottom: 14px; margin-bottom: 20px;">
    <tr>
      <td width="80" valign="middle">
        <img src="{{company_logo}}" alt="" style="height:56px; width:auto; object-fit:contain;" onerror="this.style.display='none'"/>
      </td>
      <td valign="middle" style="text-align:right;">
        <div style="font-size:18px; font-weight:700; color:#1e293b; margin-bottom:2px;">{{company_name}}</div>
        <div style="font-size:11px; color:#64748b;">{{company_address}}</div>
        <div style="font-size:11px; color:#64748b;">{{company_phone}} &nbsp;|&nbsp; {{company_email}}</div>
      </td>
    </tr>
  </table>`;
}

function refAndDate() {
  return `
  <table width="100%" cellpadding="0" cellspacing="0" style="margin-bottom:18px; font-size:12px; color:#475569;">
    <tr>
      <td>Ref. No.: <strong>{{reference_no}}</strong></td>
      <td style="text-align:right;">Date: <strong>{{document_date}}</strong></td>
    </tr>
  </table>`;
}

function toBlock() {
  return `
  <div style="font-size:13px; color:#1e293b; margin-bottom:18px; line-height:1.8;">
    <strong>To,</strong><br/>
    <strong>{{employee_name}}</strong><br/>
    {{employee_code}}<br/>
    {{designation}} — {{department}}<br/>
    {{company_name}}
  </div>`;
}

function signatureBlock() {
  return `
  <div style="margin-top:48px; font-size:13px; color:#1e293b;">
    <div style="margin-bottom:4px;">Yours sincerely,</div>
    <div style="margin-top:44px; border-top:1px solid #cbd5e1; display:inline-block; min-width:200px; padding-top:6px;">
      <strong>{{hr_signatory}}</strong><br/>
      <span style="color:#64748b;">{{hr_designation}}</span><br/>
      <span style="color:#64748b;">{{company_name}}</span>
    </div>
  </div>
  <div style="margin-top:32px; padding-top:12px; border-top:1px solid #e2e8f0; font-size:11px; color:#94a3b8; text-align:center;">
    Acknowledged by: ______________________ &nbsp;&nbsp; Date: ______________
  </div>`;
}

function wrapDoc(body) {
  return `<div style="font-family: 'Georgia', 'Times New Roman', serif; max-width:720px; margin:0 auto; padding:36px 44px; color:#1e293b; line-height:1.7; font-size:13px;">
  ${letterhead()}
  ${body}
</div>`;
}

// ─── 17 Standard Templates ────────────────────────────────────────────────────

export const STANDARD_TEMPLATES = [

  // ── 1. Offer Letter ──────────────────────────────────────────────────────────
  {
    id: 'tpl-offer-letter',
    name: 'Offer Letter',
    category: 'ONBOARDING',
    description: 'Standard employment offer with CTC, joining date, and terms of engagement.',
    is_standard: true,
    variables: ['employee_name','designation','department','gross_salary','annual_ctc','date_of_joining','effective_date','hr_signatory','probation_period','notice_period'],
    content_html: wrapDoc(`
  ${refAndDate()}
  ${toBlock()}
  <p style="font-size:13px; font-weight:700; margin-bottom:12px;">Subject: Offer of Employment — {{designation}}</p>
  <p>Dear <strong>{{employee_name}}</strong>,</p>
  <p>We are pleased to offer you the position of <strong>{{designation}}</strong> in the <strong>{{department}}</strong> department at <strong>{{company_name}}</strong>. This offer is subject to satisfactory completion of our pre-employment formalities.</p>
  <p><strong>1. Commencement of Employment</strong><br/>
  Your employment will commence on <strong>{{date_of_joining}}</strong>. Kindly confirm your acceptance of this offer by <strong>{{effective_date}}</strong>.</p>
  <p><strong>2. Compensation</strong><br/>
  Your cost-to-company (CTC) will be <strong>{{annual_ctc}}</strong> ({{gross_salary}}), subject to applicable deductions as per Indian statutory requirements (PF, ESIC, PT, TDS). The breakup of your salary structure will be shared in your appointment letter.</p>
  <p><strong>3. Probationary Period</strong><br/>
  You will be on probation for a period of <strong>{{probation_period}}</strong> from the date of joining. Your services will be confirmed subject to satisfactory performance.</p>
  <p><strong>4. Notice Period</strong><br/>
  Either party may terminate employment by providing <strong>{{notice_period}}</strong> written notice, or payment in lieu thereof, after confirmation. During probation, one week's notice shall apply.</p>
  <p><strong>5. Conditions</strong><br/>
  This offer is contingent upon successful completion of document verification, background checks, and execution of our standard Non-Disclosure Agreement. You will be required to produce original certificates at the time of joining.</p>
  <p>We look forward to welcoming you to our team and are confident that your skills and experience will be a valuable addition to {{company_name}}.</p>
  <p>Please sign and return a copy of this letter as your acknowledgement and acceptance.</p>
  ${signatureBlock()}`),
  },

  // ── 2. Appointment Letter ────────────────────────────────────────────────────
  {
    id: 'tpl-appointment-letter',
    name: 'Appointment Letter',
    category: 'ONBOARDING',
    description: 'Official appointment letter confirming employment terms after joining.',
    is_standard: true,
    variables: ['employee_name','employee_code','designation','department','gross_salary','annual_ctc','date_of_joining','probation_period','notice_period','hr_signatory'],
    content_html: wrapDoc(`
  ${refAndDate()}
  ${toBlock()}
  <p style="font-size:13px; font-weight:700; margin-bottom:12px;">Subject: Letter of Appointment — {{designation}}</p>
  <p>Dear <strong>{{employee_name}}</strong>,</p>
  <p>With reference to your application and the subsequent interview, we are pleased to appoint you as <strong>{{designation}}</strong> in the <strong>{{department}}</strong> department at <strong>{{company_name}}</strong>, with effect from <strong>{{date_of_joining}}</strong>.</p>
  <p>The terms and conditions of your employment are as follows:</p>
  <p><strong>1. Designation &amp; Reporting</strong><br/>
  You are being appointed as <strong>{{designation}}</strong> in the <strong>{{department}}</strong> department. You will report to your designated supervisor/manager and shall perform all duties assigned to you.</p>
  <p><strong>2. Compensation &amp; Benefits</strong><br/>
  Your gross monthly salary will be <strong>{{gross_salary}}</strong> (Annual CTC: <strong>{{annual_ctc}}</strong>). Statutory deductions including PF, ESIC (if applicable), Professional Tax, and TDS will be made as per prevailing laws. Salary shall be credited to your bank account on or before the last working day of each month.</p>
  <p><strong>3. Probation</strong><br/>
  You will be on probation for <strong>{{probation_period}}</strong> from the date of joining. During this period, your performance will be evaluated. Confirmation of employment shall be communicated in writing.</p>
  <p><strong>4. Working Hours</strong><br/>
  You are required to work during the hours stipulated by the Company. The standard working week is as defined in the Company's HR Policy. You may be required to work on weekends or holidays as per business requirements, for which Compensatory Off or remuneration will be provided per Company policy.</p>
  <p><strong>5. Confidentiality</strong><br/>
  You shall maintain absolute confidentiality with respect to all business, technical, commercial, or other information of a confidential nature which you may come across during your employment. This obligation survives the termination of employment.</p>
  <p><strong>6. Termination</strong><br/>
  After confirmation, either party may terminate this employment by giving <strong>{{notice_period}}</strong> written notice or payment in lieu of notice. The Company reserves the right to terminate services without notice in case of misconduct, fraud, or breach of confidentiality.</p>
  <p><strong>7. Intellectual Property</strong><br/>
  All intellectual property created by you in the course of your employment shall vest exclusively in {{company_name}}.</p>
  <p>Please sign and return the duplicate of this letter as your acceptance of the terms and conditions.</p>
  ${signatureBlock()}`),
  },

  // ── 3. Joining Confirmation Letter ───────────────────────────────────────────
  {
    id: 'tpl-joining-confirmation',
    name: 'Joining Confirmation Letter',
    category: 'ONBOARDING',
    description: 'Confirms that the employee has joined and is now on rolls.',
    is_standard: true,
    variables: ['employee_name','employee_code','designation','department','date_of_joining','hr_signatory'],
    content_html: wrapDoc(`
  ${refAndDate()}
  ${toBlock()}
  <p style="font-size:13px; font-weight:700; margin-bottom:12px;">Subject: Confirmation of Joining</p>
  <p>Dear <strong>{{employee_name}}</strong>,</p>
  <p>This is to confirm that you have successfully joined <strong>{{company_name}}</strong> as <strong>{{designation}}</strong> in the <strong>{{department}}</strong> department with effect from <strong>{{date_of_joining}}</strong>.</p>
  <p>Your Employee Code is <strong>{{employee_code}}</strong>. Please quote this in all future correspondence with the HR department.</p>
  <p>We have completed your joining formalities and your details have been updated in our HRMS. Please note the following:</p>
  <ul style="padding-left:20px; line-height:2;">
    <li>Your attendance will be tracked from <strong>{{date_of_joining}}</strong>.</li>
    <li>Your salary will be processed as per the payroll cycle once your bank account and PF/ESIC details are submitted.</li>
    <li>You are required to familiarise yourself with the Company's HR policies available on the HR portal.</li>
    <li>Any pending documents must be submitted to HR within 15 days of joining.</li>
  </ul>
  <p>We wish you a successful and rewarding career with <strong>{{company_name}}</strong>. We look forward to your valuable contributions to the organization.</p>
  ${signatureBlock()}`),
  },

  // ── 4. Probation Confirmation Letter ─────────────────────────────────────────
  {
    id: 'tpl-probation-confirmation',
    name: 'Probation Confirmation Letter',
    category: 'ONBOARDING',
    description: 'Confirms employee has completed probation and services are now confirmed.',
    is_standard: true,
    variables: ['employee_name','employee_code','designation','department','date_of_joining','probation_period','confirmation_date','hr_signatory'],
    content_html: wrapDoc(`
  ${refAndDate()}
  ${toBlock()}
  <p style="font-size:13px; font-weight:700; margin-bottom:12px;">Subject: Confirmation of Employment after Probationary Period</p>
  <p>Dear <strong>{{employee_name}}</strong>,</p>
  <p>We are pleased to inform you that upon review of your performance during the probationary period of <strong>{{probation_period}}</strong> from your date of joining (<strong>{{date_of_joining}}</strong>), the Management has decided to confirm your services as a regular employee of <strong>{{company_name}}</strong>.</p>
  <p>Your confirmation in the role of <strong>{{designation}}</strong>, <strong>{{department}}</strong> department is effective from <strong>{{confirmation_date}}</strong>.</p>
  <p>With this confirmation, the following updated terms apply:</p>
  <ul style="padding-left:20px; line-height:2;">
    <li>Notice period for resignation or termination: <strong>{{notice_period}}</strong></li>
    <li>Leave entitlements as per the Company's Leave Policy become fully applicable</li>
    <li>You will be eligible for the annual performance review cycle</li>
    <li>All other terms and conditions of employment shall remain as per your appointment letter</li>
  </ul>
  <p>We appreciate the efforts and contributions you have made during your probationary period and look forward to your continued growth with the organisation.</p>
  <p>Please sign and return the enclosed copy as acknowledgement.</p>
  ${signatureBlock()}`),
  },

  // ── 5. Increment / Revision Letter ───────────────────────────────────────────
  {
    id: 'tpl-increment-letter',
    name: 'Increment / Salary Revision Letter',
    category: 'ONGOING',
    description: 'Annual increment or salary revision letter with revised CTC.',
    is_standard: true,
    variables: ['employee_name','employee_code','designation','department','gross_salary','revised_salary','increment_amount','increment_percent','effective_date','hr_signatory'],
    content_html: wrapDoc(`
  ${refAndDate()}
  ${toBlock()}
  <p style="font-size:13px; font-weight:700; margin-bottom:12px;">Subject: Salary Revision — Effective {{effective_date}}</p>
  <p>Dear <strong>{{employee_name}}</strong>,</p>
  <p>We are pleased to inform you that the Management has reviewed your performance and contribution to <strong>{{company_name}}</strong> and has decided to revise your compensation with effect from <strong>{{effective_date}}</strong>.</p>
  <p><strong>Revised Compensation:</strong></p>
  <table width="100%" cellpadding="8" cellspacing="0" style="border-collapse:collapse; font-size:13px; margin:12px 0;">
    <tr style="background:#f1f5f9;">
      <th style="text-align:left; border:1px solid #cbd5e1; padding:8px;">Component</th>
      <th style="text-align:right; border:1px solid #cbd5e1; padding:8px;">Previous</th>
      <th style="text-align:right; border:1px solid #cbd5e1; padding:8px;">Revised</th>
    </tr>
    <tr>
      <td style="border:1px solid #cbd5e1; padding:8px;">Gross Monthly Salary</td>
      <td style="border:1px solid #cbd5e1; padding:8px; text-align:right;">{{gross_salary}}</td>
      <td style="border:1px solid #cbd5e1; padding:8px; text-align:right; font-weight:700;">{{revised_salary}}</td>
    </tr>
    <tr style="background:#f8fafc;">
      <td style="border:1px solid #cbd5e1; padding:8px;">Increment Amount</td>
      <td colspan="2" style="border:1px solid #cbd5e1; padding:8px; text-align:right; color:#16a34a; font-weight:700;">{{increment_amount}} ({{increment_percent}})</td>
    </tr>
  </table>
  <p>The revised salary structure will be shared separately by the HR department. The increment is in recognition of your performance, dedication, and contribution to the Company's success.</p>
  <p>We encourage you to continue maintaining the high standards of work and expect greater contributions from you in the coming year. We wish you continued success in your career with <strong>{{company_name}}</strong>.</p>
  <p>Please sign and return a copy of this letter as acknowledgement.</p>
  ${signatureBlock()}`),
  },

  // ── 6. Promotion Letter ──────────────────────────────────────────────────────
  {
    id: 'tpl-promotion-letter',
    name: 'Promotion Letter',
    category: 'ONGOING',
    description: 'Announces promotion to a new designation with revised salary.',
    is_standard: true,
    variables: ['employee_name','employee_code','designation','new_designation','department','revised_salary','effective_date','hr_signatory'],
    content_html: wrapDoc(`
  ${refAndDate()}
  ${toBlock()}
  <p style="font-size:13px; font-weight:700; margin-bottom:12px;">Subject: Promotion — {{designation}} to {{new_designation}}</p>
  <p>Dear <strong>{{employee_name}}</strong>,</p>
  <p>It is our pleasure to inform you that in recognition of your outstanding performance, dedication, and contributions to <strong>{{company_name}}</strong>, the Management has decided to promote you to the position of <strong>{{new_designation}}</strong> with effect from <strong>{{effective_date}}</strong>.</p>
  <p><strong>Details of Promotion:</strong></p>
  <table width="100%" cellpadding="8" cellspacing="0" style="border-collapse:collapse; font-size:13px; margin:12px 0;">
    <tr style="background:#f1f5f9;">
      <th style="text-align:left; border:1px solid #cbd5e1; padding:8px; width:40%;">Detail</th>
      <th style="text-align:left; border:1px solid #cbd5e1; padding:8px;">Previous</th>
      <th style="text-align:left; border:1px solid #cbd5e1; padding:8px;">Revised</th>
    </tr>
    <tr>
      <td style="border:1px solid #cbd5e1; padding:8px;">Designation</td>
      <td style="border:1px solid #cbd5e1; padding:8px;">{{designation}}</td>
      <td style="border:1px solid #cbd5e1; padding:8px; font-weight:700; color:#1d4ed8;">{{new_designation}}</td>
    </tr>
    <tr style="background:#f8fafc;">
      <td style="border:1px solid #cbd5e1; padding:8px;">Department</td>
      <td colspan="2" style="border:1px solid #cbd5e1; padding:8px;">{{department}}</td>
    </tr>
    <tr>
      <td style="border:1px solid #cbd5e1; padding:8px;">Revised Gross Salary</td>
      <td colspan="2" style="border:1px solid #cbd5e1; padding:8px; font-weight:700; color:#16a34a;">{{revised_salary}}</td>
    </tr>
    <tr style="background:#f8fafc;">
      <td style="border:1px solid #cbd5e1; padding:8px;">Effective Date</td>
      <td colspan="2" style="border:1px solid #cbd5e1; padding:8px;">{{effective_date}}</td>
    </tr>
  </table>
  <p>All other terms and conditions of employment shall remain unchanged unless specifically revised. A detailed revision of your salary structure will be shared by HR separately.</p>
  <p>We congratulate you on this well-deserved promotion and wish you continued success in your enhanced role. We are confident that you will continue to make significant contributions to the organisation.</p>
  ${signatureBlock()}`),
  },

  // ── 7. Transfer Letter ───────────────────────────────────────────────────────
  {
    id: 'tpl-transfer-letter',
    name: 'Transfer Letter',
    category: 'ONGOING',
    description: 'Inter-department or inter-location transfer with reporting details.',
    is_standard: true,
    variables: ['employee_name','employee_code','designation','department','new_department','new_location','effective_date','hr_signatory'],
    content_html: wrapDoc(`
  ${refAndDate()}
  ${toBlock()}
  <p style="font-size:13px; font-weight:700; margin-bottom:12px;">Subject: Transfer Order — {{department}} to {{new_department}}</p>
  <p>Dear <strong>{{employee_name}}</strong>,</p>
  <p>This is to inform you that the Management of <strong>{{company_name}}</strong> has decided to transfer you from the <strong>{{department}}</strong> department to the <strong>{{new_department}}</strong> department, located at <strong>{{new_location}}</strong>, with effect from <strong>{{effective_date}}</strong>.</p>
  <p>Please note the following with respect to your transfer:</p>
  <ul style="padding-left:20px; line-height:2;">
    <li>Your designation (<strong>{{designation}}</strong>) and compensation shall remain unchanged.</li>
    <li>You are required to complete handover of all pending responsibilities in your current department before <strong>{{effective_date}}</strong>.</li>
    <li>You will report to your new supervisor as communicated by the HR/Operations team.</li>
    <li>Relocation assistance (if applicable) shall be provided as per Company policy.</li>
    <li>All existing terms and conditions of your employment remain in force.</li>
  </ul>
  <p>This transfer is in the interest of the organisation and we request your cooperation in ensuring a smooth transition.</p>
  <p>Please acknowledge receipt of this letter by signing and returning the duplicate copy.</p>
  ${signatureBlock()}`),
  },

  // ── 8. Loan / Advance Approval Letter ────────────────────────────────────────
  {
    id: 'tpl-loan-approval',
    name: 'Salary Loan / Advance Approval Letter',
    category: 'ONGOING',
    description: 'Approves salary advance or employee loan with repayment schedule.',
    is_standard: true,
    variables: ['employee_name','employee_code','designation','department','loan_amount','loan_emi','loan_months','effective_date','hr_signatory'],
    content_html: wrapDoc(`
  ${refAndDate()}
  ${toBlock()}
  <p style="font-size:13px; font-weight:700; margin-bottom:12px;">Subject: Approval of Salary Advance / Employee Loan</p>
  <p>Dear <strong>{{employee_name}}</strong>,</p>
  <p>With reference to your application for a salary advance/loan, the Management is pleased to sanction an amount of <strong>{{loan_amount}}</strong> to you, subject to the following terms and conditions:</p>
  <table width="100%" cellpadding="8" cellspacing="0" style="border-collapse:collapse; font-size:13px; margin:12px 0;">
    <tr style="background:#f1f5f9;">
      <th style="text-align:left; border:1px solid #cbd5e1; padding:8px;">Term</th>
      <th style="text-align:left; border:1px solid #cbd5e1; padding:8px;">Details</th>
    </tr>
    <tr><td style="border:1px solid #cbd5e1; padding:8px;">Sanctioned Amount</td><td style="border:1px solid #cbd5e1; padding:8px; font-weight:700;">{{loan_amount}}</td></tr>
    <tr style="background:#f8fafc;"><td style="border:1px solid #cbd5e1; padding:8px;">Monthly Deduction (EMI)</td><td style="border:1px solid #cbd5e1; padding:8px;">{{loan_emi}} per month</td></tr>
    <tr><td style="border:1px solid #cbd5e1; padding:8px;">Repayment Period</td><td style="border:1px solid #cbd5e1; padding:8px;">{{loan_months}}</td></tr>
    <tr style="background:#f8fafc;"><td style="border:1px solid #cbd5e1; padding:8px;">Commencement of Deduction</td><td style="border:1px solid #cbd5e1; padding:8px;">From salary of {{effective_date}}</td></tr>
    <tr><td style="border:1px solid #cbd5e1; padding:8px;">Interest</td><td style="border:1px solid #cbd5e1; padding:8px;">Nil (Interest-free advance)</td></tr>
  </table>
  <p><strong>Terms &amp; Conditions:</strong></p>
  <ul style="padding-left:20px; line-height:2;">
    <li>The loan will be deducted from your monthly salary as per the schedule above.</li>
    <li>In the event of resignation or termination, the outstanding balance shall be recovered from the Full &amp; Final settlement.</li>
    <li>Any additional loan/advance request can only be made after the current loan is fully repaid.</li>
    <li>This approval is not transferable and is solely for the purpose stated in your application.</li>
  </ul>
  <p>Please sign and return the duplicate of this letter confirming your acceptance of the above terms.</p>
  ${signatureBlock()}`),
  },

  // ── 9. Resignation Acceptance Letter ─────────────────────────────────────────
  {
    id: 'tpl-resignation-acceptance',
    name: 'Resignation Acceptance Letter',
    category: 'SEPARATION',
    description: 'Formally accepts employee\'s resignation and confirms last working day.',
    is_standard: true,
    variables: ['employee_name','employee_code','designation','department','last_working_day','years_of_service','hr_signatory'],
    content_html: wrapDoc(`
  ${refAndDate()}
  ${toBlock()}
  <p style="font-size:13px; font-weight:700; margin-bottom:12px;">Subject: Acceptance of Resignation</p>
  <p>Dear <strong>{{employee_name}}</strong>,</p>
  <p>This is with reference to your resignation letter dated ___________. We acknowledge receipt of your resignation from the position of <strong>{{designation}}</strong>, <strong>{{department}}</strong> department at <strong>{{company_name}}</strong>.</p>
  <p>We regret to accept your decision to leave the organisation. After due consideration, the Management has accepted your resignation and your <strong>last working day</strong> will be <strong>{{last_working_day}}</strong>.</p>
  <p>You are requested to:</p>
  <ul style="padding-left:20px; line-height:2;">
    <li>Complete the handover of all pending assignments, projects, and responsibilities before your last working day.</li>
    <li>Return all company property, including laptop, mobile phone, ID card, access cards, keys, and any other assets.</li>
    <li>Clear all outstanding advances, loans, or dues as per Company policy.</li>
    <li>Complete the Full &amp; Final (F&amp;F) clearance process with HR before leaving.</li>
    <li>Ensure knowledge transfer is complete so your responsibilities can be transitioned smoothly.</li>
  </ul>
  <p>Your Full &amp; Final settlement, including pending salary, leave encashment (if applicable), and other dues, shall be processed within the stipulated period after completion of all clearances.</p>
  <p>We appreciate your contributions to <strong>{{company_name}}</strong> over your service tenure of <strong>{{years_of_service}}</strong> and wish you all the best in your future endeavours.</p>
  ${signatureBlock()}`),
  },

  // ── 10. Relieving Letter ─────────────────────────────────────────────────────
  {
    id: 'tpl-relieving-letter',
    name: 'Relieving Letter',
    category: 'SEPARATION',
    description: 'Issued on last working day confirming employee has been relieved from duties.',
    is_standard: true,
    variables: ['employee_name','employee_code','designation','department','date_of_joining','last_working_day','years_of_service','hr_signatory'],
    content_html: wrapDoc(`
  ${refAndDate()}
  <p style="text-align:center; font-size:16px; font-weight:700; color:#1e293b; margin-bottom:24px; letter-spacing:0.5px; text-transform:uppercase; border-bottom:1px solid #e2e8f0; padding-bottom:12px;">RELIEVING LETTER</p>
  <p>To Whomsoever It May Concern,</p>
  <p>This is to certify that <strong>{{employee_name}}</strong> (Employee Code: <strong>{{employee_code}}</strong>) was employed with <strong>{{company_name}}</strong> as <strong>{{designation}}</strong> in the <strong>{{department}}</strong> department from <strong>{{date_of_joining}}</strong> to <strong>{{last_working_day}}</strong> (tenure: {{years_of_service}}).</p>
  <p>{{employee_name}} has been formally relieved from all duties and responsibilities with effect from the close of business on <strong>{{last_working_day}}</strong>.</p>
  <p>During the period of employment, {{employee_name}} has:</p>
  <ul style="padding-left:20px; line-height:2;">
    <li>Completed all handover procedures satisfactorily.</li>
    <li>Returned all company assets and property.</li>
    <li>Obtained clearance from all relevant departments.</li>
  </ul>
  <p>All dues and obligations between {{employee_name}} and {{company_name}} have been settled to mutual satisfaction as part of the Full &amp; Final settlement.</p>
  <p>We wish {{employee_name}} all the best in future endeavours.</p>
  <p style="margin-top:16px; font-style:italic; color:#64748b; font-size:12px;">This letter is issued on the specific request of the employee and does not confer any obligation on {{company_name}} beyond what is stated herein.</p>
  ${signatureBlock()}`),
  },

  // ── 11. Experience Certificate ───────────────────────────────────────────────
  {
    id: 'tpl-experience-certificate',
    name: 'Experience Certificate',
    category: 'SEPARATION',
    description: 'Certifies employment duration, designation, and conduct for future employers.',
    is_standard: true,
    variables: ['employee_name','employee_code','designation','department','date_of_joining','last_working_day','years_of_service','employment_type','hr_signatory'],
    content_html: wrapDoc(`
  ${refAndDate()}
  <p style="text-align:center; font-size:16px; font-weight:700; color:#1e293b; margin-bottom:24px; letter-spacing:0.5px; text-transform:uppercase; border-bottom:1px solid #e2e8f0; padding-bottom:12px;">EXPERIENCE CERTIFICATE</p>
  <p>To Whomsoever It May Concern,</p>
  <p>This is to certify that <strong>{{employee_name}}</strong> (Employee Code: <strong>{{employee_code}}</strong>) was employed with <strong>{{company_name}}</strong> as <strong>{{designation}} ({{employment_type}})</strong> in the <strong>{{department}}</strong> department from <strong>{{date_of_joining}}</strong> to <strong>{{last_working_day}}</strong>.</p>
  <p>During the tenure of <strong>{{years_of_service}}</strong> with our organisation, <strong>{{employee_name}}</strong> demonstrated:</p>
  <ul style="padding-left:20px; line-height:2;">
    <li>Professional competence and technical skills relevant to the role.</li>
    <li>Consistent dedication and commitment to quality of work.</li>
    <li>Good interpersonal skills and ability to work collaboratively with team members.</li>
    <li>Adherence to Company policies, processes, and professional conduct standards.</li>
  </ul>
  <p>We found <strong>{{employee_name}}</strong> to be a sincere, hardworking, and reliable professional. Their conduct and behaviour throughout the employment period were found to be satisfactory.</p>
  <p>We wish <strong>{{employee_name}}</strong> all the best in their future endeavours and highly recommend them to any prospective employer.</p>
  <p style="margin-top:16px; font-style:italic; color:#64748b; font-size:12px;">This certificate is issued on specific request and for the purpose of employment elsewhere.</p>
  ${signatureBlock()}`),
  },

  // ── 12. Service Certificate ──────────────────────────────────────────────────
  {
    id: 'tpl-service-certificate',
    name: 'Service Certificate',
    category: 'SEPARATION',
    description: 'Simple certificate confirming period of service for statutory or banking purposes.',
    is_standard: true,
    variables: ['employee_name','employee_code','designation','department','date_of_joining','last_working_day','gross_salary','hr_signatory'],
    content_html: wrapDoc(`
  ${refAndDate()}
  <p style="text-align:center; font-size:16px; font-weight:700; color:#1e293b; margin-bottom:24px; letter-spacing:0.5px; text-transform:uppercase; border-bottom:1px solid #e2e8f0; padding-bottom:12px;">SERVICE CERTIFICATE</p>
  <p>To Whomsoever It May Concern,</p>
  <p>This is to certify that <strong>{{employee_name}}</strong> (Employee Code: <strong>{{employee_code}}</strong>) was an employee of <strong>{{company_name}}</strong>.</p>
  <table width="100%" cellpadding="8" cellspacing="0" style="border-collapse:collapse; font-size:13px; margin:16px 0;">
    <tr><td style="border:1px solid #cbd5e1; padding:10px; background:#f8fafc; width:40%;"><strong>Employee Name</strong></td><td style="border:1px solid #cbd5e1; padding:10px;">{{employee_name}}</td></tr>
    <tr><td style="border:1px solid #cbd5e1; padding:10px; background:#f8fafc;"><strong>Employee Code</strong></td><td style="border:1px solid #cbd5e1; padding:10px;">{{employee_code}}</td></tr>
    <tr><td style="border:1px solid #cbd5e1; padding:10px; background:#f8fafc;"><strong>Designation</strong></td><td style="border:1px solid #cbd5e1; padding:10px;">{{designation}}</td></tr>
    <tr><td style="border:1px solid #cbd5e1; padding:10px; background:#f8fafc;"><strong>Department</strong></td><td style="border:1px solid #cbd5e1; padding:10px;">{{department}}</td></tr>
    <tr><td style="border:1px solid #cbd5e1; padding:10px; background:#f8fafc;"><strong>Date of Joining</strong></td><td style="border:1px solid #cbd5e1; padding:10px;">{{date_of_joining}}</td></tr>
    <tr><td style="border:1px solid #cbd5e1; padding:10px; background:#f8fafc;"><strong>Last Working Day</strong></td><td style="border:1px solid #cbd5e1; padding:10px;">{{last_working_day}}</td></tr>
    <tr><td style="border:1px solid #cbd5e1; padding:10px; background:#f8fafc;"><strong>Last Drawn Salary</strong></td><td style="border:1px solid #cbd5e1; padding:10px;">{{gross_salary}}</td></tr>
  </table>
  <p>This certificate is issued for the purpose requested by the employee and for general reference to any bank, institution, or future employer.</p>
  ${signatureBlock()}`),
  },

  // ── 13. NOC Letter ───────────────────────────────────────────────────────────
  {
    id: 'tpl-noc-letter',
    name: 'No Objection Certificate (NOC)',
    category: 'SEPARATION',
    description: 'NOC for visa, further studies, bank loan, or other external purposes.',
    is_standard: true,
    variables: ['employee_name','employee_code','designation','department','date_of_joining','hr_signatory'],
    content_html: wrapDoc(`
  ${refAndDate()}
  <p style="text-align:center; font-size:16px; font-weight:700; color:#1e293b; margin-bottom:24px; letter-spacing:0.5px; text-transform:uppercase; border-bottom:1px solid #e2e8f0; padding-bottom:12px;">NO OBJECTION CERTIFICATE</p>
  <p>To Whomsoever It May Concern,</p>
  <p>This is to certify that <strong>{{employee_name}}</strong> (Employee Code: <strong>{{employee_code}}</strong>) is/was employed with <strong>{{company_name}}</strong> as <strong>{{designation}}</strong> in the <strong>{{department}}</strong> department since <strong>{{date_of_joining}}</strong>.</p>
  <p><strong>{{company_name}}</strong> has <strong>No Objection</strong> to <strong>{{employee_name}}</strong> applying for / pursuing ___________.</p>
  <p>This certificate is issued on the specific request of the employee for the purpose mentioned above. It does not imply any financial or legal obligation on the part of {{company_name}} beyond the scope of normal employment.</p>
  <p>For any verification, please contact us at <strong>{{company_email}}</strong> or <strong>{{company_phone}}</strong>.</p>
  ${signatureBlock()}`),
  },

  // ── 14. Warning Letter ───────────────────────────────────────────────────────
  {
    id: 'tpl-warning-letter',
    name: 'Warning Letter',
    category: 'DISCIPLINARY',
    description: 'First or second formal warning for misconduct or policy violation.',
    is_standard: true,
    variables: ['employee_name','employee_code','designation','department','warning_subject','warning_incident','warning_date','hr_signatory'],
    content_html: wrapDoc(`
  ${refAndDate()}
  ${toBlock()}
  <p style="font-size:13px; font-weight:700; color:#dc2626; margin-bottom:12px;">Subject: Warning Letter — {{warning_subject}}</p>
  <p>Dear <strong>{{employee_name}}</strong>,</p>
  <p>This letter serves as a formal warning regarding your conduct and behaviour which is in violation of the Company's policies and code of conduct.</p>
  <p><strong>Nature of Violation:</strong></p>
  <div style="background:#fef2f2; border-left:4px solid #dc2626; padding:12px 16px; margin:12px 0; border-radius:0 4px 4px 0; font-size:13px;">
    {{warning_incident}}
  </div>
  <p>The above act/behaviour/conduct is unacceptable and in direct contravention of the Company's Code of Conduct and HR policies. Such behaviour adversely affects the team, the work environment, and the Company's reputation.</p>
  <p>You are hereby warned that a recurrence of such behaviour or any other misconduct shall attract serious disciplinary action, which may include suspension or termination of your services.</p>
  <p>We strongly advise you to:</p>
  <ul style="padding-left:20px; line-height:2;">
    <li>Adhere strictly to the Company's policies and code of conduct henceforth.</li>
    <li>Maintain professional standards at the workplace at all times.</li>
    <li>Seek guidance from your supervisor or HR if you are unclear about any policy.</li>
  </ul>
  <p>This warning letter will be placed on record in your personal file. You are advised to treat this as a serious matter.</p>
  <p>Please sign and return the acknowledgement copy. Your signature does not indicate agreement with the contents; it merely confirms receipt of this letter.</p>
  ${signatureBlock()}`),
  },

  // ── 15. Show Cause Notice ────────────────────────────────────────────────────
  {
    id: 'tpl-show-cause-notice',
    name: 'Show Cause Notice',
    category: 'DISCIPLINARY',
    description: 'Requires employee to explain conduct before disciplinary action is taken.',
    is_standard: true,
    variables: ['employee_name','employee_code','designation','department','warning_subject','warning_incident','reply_by_date','hr_signatory'],
    content_html: wrapDoc(`
  ${refAndDate()}
  ${toBlock()}
  <p style="font-size:13px; font-weight:700; color:#b45309; margin-bottom:12px;">Subject: Show Cause Notice — {{warning_subject}}</p>
  <p>Dear <strong>{{employee_name}}</strong>,</p>
  <p>This notice is issued to you in connection with the following act/omission/behaviour which has come to the attention of the Management:</p>
  <div style="background:#fffbeb; border-left:4px solid #d97706; padding:12px 16px; margin:12px 0; border-radius:0 4px 4px 0; font-size:13px;">
    {{warning_incident}}
  </div>
  <p>The said act/omission/behaviour constitutes a violation of the Company's Code of Conduct / Standing Orders / HR Policy and is liable for disciplinary action.</p>
  <p>Before the Management proceeds to take any disciplinary action against you, you are hereby given an opportunity to explain your conduct and furnish reasons as to why disciplinary action should not be initiated against you.</p>
  <p>You are directed to submit your written explanation/response to this notice within <strong>7 working days</strong> i.e. on or before <strong>{{reply_by_date}}</strong> to the HR Department.</p>
  <p><strong>Please note:</strong></p>
  <ul style="padding-left:20px; line-height:2;">
    <li>Failure to respond by the stipulated deadline shall be construed as admission of the act/omission.</li>
    <li>The Management reserves the right to proceed with disciplinary proceedings ex-parte if no response is received.</li>
    <li>Disciplinary action may include warning, suspension, demotion, or termination of employment.</li>
  </ul>
  <p>This notice is issued without prejudice to the rights and remedies available to the Company.</p>
  ${signatureBlock()}`),
  },

  // ── 16. Termination Letter ───────────────────────────────────────────────────
  {
    id: 'tpl-termination-letter',
    name: 'Termination Letter',
    category: 'DISCIPLINARY',
    description: 'Formally terminates employment for misconduct, poor performance, or redundancy.',
    is_standard: true,
    variables: ['employee_name','employee_code','designation','department','termination_reason','last_working_day','hr_signatory'],
    content_html: wrapDoc(`
  ${refAndDate()}
  ${toBlock()}
  <p style="font-size:13px; font-weight:700; color:#dc2626; margin-bottom:12px;">Subject: Termination of Employment — {{designation}}</p>
  <p>Dear <strong>{{employee_name}}</strong>,</p>
  <p>This letter formally informs you that your employment with <strong>{{company_name}}</strong> as <strong>{{designation}}</strong> in the <strong>{{department}}</strong> department is terminated with effect from <strong>{{last_working_day}}</strong>.</p>
  <p><strong>Reason for Termination:</strong></p>
  <div style="background:#fef2f2; border-left:4px solid #dc2626; padding:12px 16px; margin:12px 0; border-radius:0 4px 4px 0; font-size:13px;">
    {{termination_reason}}
  </div>
  <p>Please note the following with respect to your termination:</p>
  <ul style="padding-left:20px; line-height:2;">
    <li>You are required to immediately return all company property including laptop, mobile, ID card, access cards, keys, and any other assets.</li>
    <li>You are prohibited from accessing Company premises, systems, data, or accounts after the effective date of termination.</li>
    <li>Your Full &amp; Final settlement will be processed as per Company policy after completion of clearances and recovery of all dues.</li>
    <li>The confidentiality obligations under your employment agreement remain in full force after termination.</li>
    <li>You are advised not to make any statements (oral or written) about the Company or its operations to external parties.</li>
  </ul>
  <p>HR will contact you to complete the exit formalities and Full &amp; Final settlement process.</p>
  <p style="margin-top:16px; font-style:italic; color:#64748b; font-size:12px;">This letter is issued on authority of the Management of {{company_name}} and serves as formal communication of the termination.</p>
  ${signatureBlock()}`),
  },

  // ── 17. Employee Verification / Salary Certificate ───────────────────────────
  {
    id: 'tpl-salary-certificate',
    name: 'Employee Verification / Salary Certificate',
    category: 'OTHER',
    description: 'For bank loan, visa, or rental purposes — certifies employment and salary.',
    is_standard: true,
    variables: ['employee_name','employee_code','designation','department','date_of_joining','gross_salary','annual_ctc','hr_signatory'],
    content_html: wrapDoc(`
  ${refAndDate()}
  <p style="text-align:center; font-size:16px; font-weight:700; color:#1e293b; margin-bottom:24px; letter-spacing:0.5px; text-transform:uppercase; border-bottom:1px solid #e2e8f0; padding-bottom:12px;">EMPLOYMENT &amp; SALARY VERIFICATION CERTIFICATE</p>
  <p>To Whomsoever It May Concern,</p>
  <p>This is to certify that <strong>{{employee_name}}</strong> (Employee Code: <strong>{{employee_code}}</strong>) is a bonafide employee of <strong>{{company_name}}</strong> and the following details are furnished for verification purposes:</p>
  <table width="100%" cellpadding="10" cellspacing="0" style="border-collapse:collapse; font-size:13px; margin:16px 0;">
    <tr><td style="border:1px solid #cbd5e1; background:#f8fafc; width:45%;"><strong>Full Name</strong></td><td style="border:1px solid #cbd5e1; padding:10px;">{{employee_name}}</td></tr>
    <tr><td style="border:1px solid #cbd5e1; background:#f8fafc; padding:10px;"><strong>Employee ID / Code</strong></td><td style="border:1px solid #cbd5e1; padding:10px;">{{employee_code}}</td></tr>
    <tr><td style="border:1px solid #cbd5e1; background:#f8fafc; padding:10px;"><strong>Designation</strong></td><td style="border:1px solid #cbd5e1; padding:10px;">{{designation}}</td></tr>
    <tr><td style="border:1px solid #cbd5e1; background:#f8fafc; padding:10px;"><strong>Department</strong></td><td style="border:1px solid #cbd5e1; padding:10px;">{{department}}</td></tr>
    <tr><td style="border:1px solid #cbd5e1; background:#f8fafc; padding:10px;"><strong>Date of Joining</strong></td><td style="border:1px solid #cbd5e1; padding:10px;">{{date_of_joining}}</td></tr>
    <tr><td style="border:1px solid #cbd5e1; background:#f8fafc; padding:10px;"><strong>Employment Status</strong></td><td style="border:1px solid #cbd5e1; padding:10px; color:#16a34a; font-weight:700;">Active / Regular</td></tr>
    <tr><td style="border:1px solid #cbd5e1; background:#f8fafc; padding:10px;"><strong>Gross Monthly Salary</strong></td><td style="border:1px solid #cbd5e1; padding:10px; font-weight:700;">{{gross_salary}}</td></tr>
    <tr><td style="border:1px solid #cbd5e1; background:#f8fafc; padding:10px;"><strong>Annual CTC</strong></td><td style="border:1px solid #cbd5e1; padding:10px;">{{annual_ctc}}</td></tr>
  </table>
  <p>The above information is furnished in good faith for the specific purpose requested. For any official verification or clarification, please contact: <strong>{{company_email}}</strong> | <strong>{{company_phone}}</strong>.</p>
  <p style="font-style:italic; color:#64748b; font-size:12px;">This certificate is issued on the specific request of the employee and is valid for 90 days from the date of issue.</p>
  ${signatureBlock()}`),
  },
];

// ─── Category metadata ────────────────────────────────────────────────────────

export const TEMPLATE_CATEGORIES = [
  { code: 'ONBOARDING',   label: 'Onboarding',   color: '#2563eb', bg: '#eff6ff' },
  { code: 'ONGOING',      label: 'Ongoing',      color: '#7c3aed', bg: '#f5f3ff' },
  { code: 'SEPARATION',   label: 'Separation',   color: '#d97706', bg: '#fffbeb' },
  { code: 'DISCIPLINARY', label: 'Disciplinary', color: '#dc2626', bg: '#fef2f2' },
  { code: 'OTHER',        label: 'Other',        color: '#16a34a', bg: '#f0fdf4' },
];

export function getCategoryMeta(code) {
  return TEMPLATE_CATEGORIES.find(c => c.code === code) || TEMPLATE_CATEGORIES[4];
}

/** Builds a print-ready full HTML document from template content_html */
export function buildPrintDocument(contentHtml, title = 'Document') {
  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8"/>
<title>${title}</title>
<style>
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { background: white; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  @page { margin: 0; size: A4; }
  @media print { body { margin: 0; } }
  img { max-width: 100%; }
  ul { list-style: disc; }
  ol { list-style: decimal; }
</style>
</head>
<body>${contentHtml}</body>
</html>`;
}
